import type { FunctionComponent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { analytics } from '../Configs/analytics'
import type { Category } from '../Configs/categories'
import { useGuidedPurchaseItemSpec } from '../hooks/useGuidedPurchaseItemSpec'
import { QuestionScreen } from '../QuestionScreen/QuestionScreen'
import { questionConfigsByCategory } from './QuestionsConfig'

export type GuidedPurchaseJourneyStep = {
  name: string
  component: FunctionComponent<{ onSkip?: () => void }>
  trackingCallback?: (rest?: Record<string, string>) => void
  title?: string
  nextStep?: string
  required?: boolean
  isAnswered?: boolean
}

export const useQuestionSteps = ({ category }: { category: Category }) => {
  const questionConfigs = questionConfigsByCategory[category]
  const itemSpec = useGuidedPurchaseItemSpec()
  const [selectedIndexes, setSelectedIndexes] = useState<(number | undefined)[]>(
    Array(questionConfigs.length).fill(undefined)
  )
  const buildSkippedStepSet = useCallback(
    (indexes: (number | undefined)[]) => {
      const nextSkippedSteps = new Set<string>()

      indexes.forEach((selectedOptionIndex, index) => {
        const stepName = questionConfigs[index].name

        if (nextSkippedSteps.has(stepName)) {
          return
        }

        if (selectedOptionIndex === undefined) {
          return
        }

        questionConfigs[index].options[selectedOptionIndex].skipSteps?.forEach((skippedStepName) => {
          nextSkippedSteps.add(skippedStepName)
        })
      })

      return nextSkippedSteps
    },
    [questionConfigs]
  )

  const skippedSteps = useMemo(
    () => buildSkippedStepSet(selectedIndexes),
    [buildSkippedStepSet, selectedIndexes]
  )

  const filters = useMemo<Record<string, { [code: string]: string[] }>>(() => {
    return selectedIndexes.reduce<Record<string, { [code: string]: string[] }>>(
      (acc, selectedOptionIndex, index) => {
        if (selectedOptionIndex === undefined || skippedSteps.has(questionConfigs[index].name)) {
          return acc
        }

        const optionFilter = questionConfigs[index].options[selectedOptionIndex].filter
        if (!optionFilter) {
          return acc
        }

        return {
          ...acc,
          ...(optionFilter as Record<string, { [code: string]: string[] }>),
        }
      },
      {}
    )
  }, [questionConfigs, selectedIndexes, skippedSteps])

  const handleOptionSelect = useCallback((questionIndex: number, optionIndex: number) => {
    setSelectedIndexes((prevSelectedIndexes) => {
      const newSelectedIndexes = [...prevSelectedIndexes]
      newSelectedIndexes[questionIndex] = optionIndex

      // When a user changes an earlier answer, clear later answers.
      for (let i = questionIndex + 1; i < newSelectedIndexes.length; i++) {
        newSelectedIndexes[i] = undefined
      }
      return newSelectedIndexes
    })
  }, [])

  const clearSelectionsFromIndex = useCallback((startIndex: number) => {
    setSelectedIndexes((prevSelectedIndexes) =>
      prevSelectedIndexes.map((value, index) => (index >= startIndex ? undefined : value))
    )
  }, [])

  const steps = useMemo<GuidedPurchaseJourneyStep[]>(() => {
    return questionConfigs.map((config, index) => {
      const selectedOptionIndex = selectedIndexes[index]
      const nextStep =
        selectedOptionIndex !== undefined ? config.options[selectedOptionIndex].nextStep : undefined

      return {
        name: config.name,
        ...(config.title ? { title: config.title } : {}),
        nextStep,
        required: config.required,
        isAnswered: selectedIndexes[index] !== undefined,
        component: ({ onSkip }: { onSkip?: () => void }) => (
          <QuestionScreen
            question={config.question}
            options={config.options}
            screenName={`step ${index + 1}`}
            selectedIndex={selectedIndexes[index]}
            category={category}
            callback={(optionIndex: number) => handleOptionSelect(index, optionIndex)}
            onSkip={onSkip}
          />
        ),
        trackingCallback: (rest?: Record<string, string>) => {
          const optionIndex = selectedIndexes[index] ?? -1

          dynamicGa4DataLayerPush({
            event: 'personalisation_tool_question',
            action_group: 'question',
            item_index: optionIndex + 1,
            item_parent_text: `step ${index + 1}`,
            item_question: questionConfigs[index].shortQuestion,
            item_answer:
              questionConfigs[index].options?.[optionIndex]?.label ?? 'NO ANSWER SELECTED',
            item_spec: itemSpec,
            item_group: 'personalisation tools',
            tool_name: analytics[category].tool_name,
            item_personalisation: 'false',
            ...rest,
          })
        },
      }
    })
  }, [questionConfigs, selectedIndexes, category, handleOptionSelect, itemSpec])

  return {
    steps,
    filters,
    skippedSteps,
    selectedIndexes,
    clearSelectionsFromIndex,
  }
}
